import { useCallback, useState } from 'react'
import { useInterval } from 'react-use'
import { useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'

import Styles from './InviteTeam.module.scss'
import cx from 'classnames'

import { ReactComponent as LinkIcon } from 'assets/icons/link16.svg'
import { ReactComponent as GoogleLogo } from 'components/GoogleButton/GoogleLogo.svg'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { frontendOrigin } from 'utils/frontendOrigin'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FetchSubscriptionResponse } from 'api/clients/WorkspaceClient'
import { Button } from 'blaze-ui/Button'
import { Check } from 'blaze-ui/icons/16'
import { Heading } from 'almanac-ui/Heading/Heading'
import { useModals } from 'almanac-ui/Modal'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { LockOpen3 } from 'blaze-ui/icons/24/LockOpen3'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'

export const InviteByLink = ({
  showImportScreen,
  currentPlan,
  onClose,
}: {
  showImportScreen: () => void
  currentPlan: FetchSubscriptionResponse
  onClose: () => void
}) => {
  const workspace = useCurrentWorkspace()
  const [isCopied, setIsCopied] = useState(false)
  const location = useLocation()
  const { openModal } = useModals()

  const isFreeOrStarterPlan =
    currentPlan?.plan === PaymentPlanKey.Free ||
    currentPlan?.plan === PaymentPlanKey.BlazeAutopilotStarter ||
    currentPlan?.status === 'canceled'

  useInterval(() => {
    if (isCopied) {
      setIsCopied(false)
    }
  }, 3000)

  const selectInput = useCallback((inputElement: any) => {
    if (inputElement) {
      setTimeout(() => {
        inputElement.select()
      }, 50)
    }
  }, [])

  if (!workspace) return null

  const shareLink = `${frontendOrigin()}/invite/workspace/${workspace.invitation_token}`

  const copyShareLink = () => {
    if (!workspace) {
      showToastMessage('Unable to copy share link', 'error')
      return
    }
    navigator.clipboard.writeText(shareLink)
    setIsCopied(true)
    showToastMessage('Link Copied!', 'success')
  }

  const handleUpgradeToGrowth = () => {
    openModal(AutopilotUpgradeEntryModal, {
      variant: 'invite_team',
      targetPlanKey: PaymentPlanKey.BlazeAutopilotGrowth,
    })
  }

  return (
    <>
      <div className={Styles.linkContainer}>
        <div className={Styles.shareLinkContainer}>
          <Heading level={3}>Share this link with your team mates</Heading>
          <div className={Styles.copyLinkContainer}>
            <input
              value={isFreeOrStarterPlan ? '' : shareLink}
              placeholder="Link will appear here when you upgrade"
              ref={isFreeOrStarterPlan ? undefined : selectInput}
              readOnly
            />
            <Button
              frontIcon={isCopied ? Check : LinkIcon}
              size="md"
              onPress={copyShareLink}
              isDisabled={isFreeOrStarterPlan || !workspace?.invitation_token_enabled}
            >
              <span className={Styles.linkButtonText}>{isCopied ? 'Copied!' : 'Copy Link'}</span>
            </Button>
          </div>
        </div>

        {!workspace?.invitation_token_enabled && (
          <span className={Styles.subtext}>
            <span data-testid="enable-share-link-message">
              Share link must be enabled in <Link to={`${location.pathname}?settings=members`}>settings</Link>.
            </span>
          </span>
        )}

        <div className={Styles.importSection}>
          <Heading level={3}>or import all your contacts at once</Heading>
          <Button
            frontIcon={GoogleLogo}
            size="lg"
            onPress={showImportScreen}
            variant="secondary"
            isDisabled={isFreeOrStarterPlan}
            className={Styles.googleContactsButton}
          >
            Import from Google Contacts
          </Button>
        </div>
      </div>
      <div className={Styles.inviteByEmailFooter}>
        <Button size="lg" variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <div className={Styles.inviteByEmailFooterRight}>
          {isFreeOrStarterPlan ? (
            <Button
              onPress={handleUpgradeToGrowth}
              size="lg"
              frontIcon={LockOpen3}
              className={cx(Styles.upgradeToGrowthButton)}
            >
              Upgrade to Growth to Invite Your Team
            </Button>
          ) : (
            <Button size="lg" onPress={copyShareLink} isDisabled={!workspace?.invitation_token_enabled}>
              Copy Link
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
